import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Target, CheckCircle2, AlertCircle, ChevronRight,
  Briefcase, Globe, IndianRupee, GraduationCap, Building2,
  ArrowUpRight, Star, Layers, BookOpen, Zap, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';

// ─── helpers ──────────────────────────────────────────────────────────────────
const PALETTE = [
  ['#1a3884', '#4f7dff'],
  ['#0e4f7a', '#3ab5e6'],
  ['#2d1e6b', '#8b5cf6'],
  ['#0f4c2a', '#34d399'],
];
const cardColor = (name = '') => {
  const hash = name.split('').reduce((a, c) => c.charCodeAt(0) + a, 0);
  return PALETTE[hash % PALETTE.length];
};
const initials = (name = '') =>
  name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

// Normalize skill name from various shapes
const skillName = (s) => (typeof s === 'string' ? s : s?.skill_name || s?.name || '');

// Compute match % between student skills and job required skills
const computeMatch = (studentSkills = [], job) => {
  const required = (job.eligibility?.requiredSkills || []).map(s => skillName(s).toLowerCase());
  if (required.length === 0) return { pct: 75, matched: [], missing: [] }; // no requirements = high match
  const studentSet = new Set(studentSkills.map(s => skillName(s).toLowerCase()));
  const matched = required.filter(r => studentSet.has(r));
  const missing = required.filter(r => !studentSet.has(r));
  return {
    pct: Math.round((matched.length / required.length) * 100),
    matched,
    missing
  };
};

const matchBand = (pct) => {
  if (pct >= 80) return { label: 'Strong Match', color: '#10b981', bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400' };
  if (pct >= 50) return { label: 'Good Match', color: '#3b82f6', bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400' };
  if (pct >= 30) return { label: 'Partial Match', color: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400' };
  return { label: 'Low Match', color: '#f43f5e', bg: 'bg-rose-50 dark:bg-rose-950/30', text: 'text-rose-700 dark:text-rose-400' };
};

// ─── PrepCheckItem ─────────────────────────────────────────────────────────────
const PrepCheckItem = ({ done, label, sub }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${done ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`}>
      {done ? <CheckCircle2 size={12} className="text-white" /> : <span className="w-2 h-2 rounded-full bg-white/60" />}
    </div>
    <div>
      <p className={`text-sm font-bold ${done ? 'text-gray-800 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>{label}</p>
      {sub && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── CompanyMatchCard ──────────────────────────────────────────────────────────
const CompanyMatchCard = ({ booth, studentSkills, cgpa, isDark }) => {
  const [imgErr, setImgErr] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { company, jobs } = booth;
  const name = company?.name || company?.fullName || company?.qualification || 'Unknown Company';
  const [from, to] = cardColor(name);

  const eligibleJobs = jobs.filter(job => {
    const minCGPA = job.eligibility?.minCGPA || 0;
    return !cgpa || cgpa >= minCGPA;
  });

  // Best match across all jobs
  const bestMatch = jobs.reduce((best, job) => {
    const { pct } = computeMatch(studentSkills, job);
    return pct > best ? pct : best;
  }, 0);

  const band = matchBand(bestMatch);

  return (
    <div
      className="rounded-2xl border overflow-hidden transition-all duration-200"
      style={{ borderColor: isDark ? 'rgba(26,56,132,0.3)' : '#e2eaff', background: isDark ? '#002147' : '#fff' }}
    >
      {/* Top bar with gradient */}
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${from}, ${to})` }} />

      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          {/* Logo */}
          <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center shrink-0">
            {company?.logo && !imgErr ? (
              <img src={company.logo} alt={name} onError={() => setImgErr(true)} className="w-full h-full object-contain p-1" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-black text-white text-sm" style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
                {initials(name)}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-black text-base text-gray-900 dark:text-white leading-tight truncate">{name}</h4>
            {company?.industry && (
              <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">{company.industry}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Match score pill */}
              <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-full ${band.bg} ${band.text}`}>
                {bestMatch}% {band.label}
              </span>
              {/* Eligible count */}
              <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${eligibleJobs.length > 0 ? 'bg-[#1a3884]/8 dark:bg-[#1a3884]/25 text-[#1a3884] dark:text-[#4f7dff]' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                {eligibleJobs.length}/{jobs.length} roles eligible
              </span>
            </div>
          </div>

          {/* Match arc */}
          <div className="relative w-12 h-12 shrink-0">
            <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
              <circle cx="20" cy="20" r="16" fill="none" stroke={isDark ? '#1a3884' : '#e2eaff'} strokeWidth="4" />
              <circle cx="20" cy="20" r="16" fill="none" stroke={band.color} strokeWidth="4"
                strokeDasharray={`${(bestMatch / 100) * 100.53} 100.53`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black" style={{ color: band.color }}>
              {bestMatch}%
            </span>
          </div>
        </div>

        {/* Job list (collapsed by default) */}
        {jobs.length > 0 && (
          <>
            <button
              onClick={() => setExpanded(e => !e)}
              className="w-full flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800 text-[12px] font-bold text-slate-600 dark:text-slate-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <Briefcase size={12} />
                {jobs.length} Position{jobs.length !== 1 ? 's' : ''} Available
              </span>
              <ChevronRight size={13} className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
            </button>

            {expanded && (
              <div className="mt-2.5 space-y-2">
                {jobs.map((job, i) => {
                  const { pct, missing } = computeMatch(studentSkills, job);
                  const jband = matchBand(pct);
                  const minCGPA = job.eligibility?.minCGPA || 0;
                  const cgpaOk = !cgpa || cgpa >= minCGPA;

                  return (
                    <div key={job._id || i} className="p-3 rounded-xl border" style={{ background: isDark ? '#001430' : '#f8fbff', borderColor: isDark ? 'rgba(26,56,132,0.2)' : '#e2eaff' }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[13px] text-gray-900 dark:text-white truncate">{job.title}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                            {job.salaryPackage && (
                              <span className="flex items-center gap-0.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                                <IndianRupee size={9} />{job.salaryPackage}
                              </span>
                            )}
                            {job.jobType && <span className="text-[10px] text-slate-400">{job.jobType}</span>}
                            {job.workMode && <span className="text-[10px] text-slate-400">{job.workMode}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${jband.bg} ${jband.text}`}>{pct}%</span>
                          {!cgpaOk && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400">
                              CGPA {minCGPA}+
                            </span>
                          )}
                        </div>
                      </div>
                      {missing.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[9px] font-black uppercase tracking-wider text-slate-400 mb-1">Skills to improve:</p>
                          <div className="flex flex-wrap gap-1">
                            {missing.slice(0, 4).map((sk, si) => (
                              <span key={si} className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/30 capitalize">
                                {sk}
                              </span>
                            ))}
                            {missing.length > 4 && <span className="text-[9px] text-slate-400">+{missing.length - 4}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const StudentPrepKit = ({ fairId, student, isDark }) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('match'); // 'match' | 'jobs'

  const studentSkills = student?.skills || student?.placementProfile?.skills || [];
  const cgpa = student?.cgpa || student?.academic?.cgpa || student?.registration?.cgpa || 0;
  const degree = student?.degree?.abbreviation || student?.degree?.fullName || '';
  const hasResume = !!(student?.placementProfile?.resumeUrl || student?.resumeUrl);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await api.get(`/placements/job-fairs/${fairId}/floor-map`);
      if (res.success) setMapData(res.data);
    } catch (e) {
      setError('Could not load prep kit data');
    } finally {
      setLoading(false);
    }
  }, [fairId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[280px]">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-3">
            <div className="absolute inset-0 rounded-full border-4 border-[#1a3884]/20" />
            <div className="absolute inset-0 rounded-full border-4 border-t-[#1a3884] animate-spin" />
          </div>
          <p className="text-sm font-semibold text-slate-400">Generating your prep kit…</p>
        </div>
      </div>
    );
  }

  if (error || !mapData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[240px] gap-3">
        <AlertCircle size={28} className="text-rose-400" />
        <p className="text-sm text-slate-500">{error || 'No data available'}</p>
        <button onClick={fetchData} className="flex items-center gap-2 text-sm font-bold text-[#1a3884] dark:text-[#4f7dff]">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  const booths = mapData.booths.filter(b => b.company && b.jobs.length > 0);

  // Compute best match for each booth for sorting
  const boothsWithMatch = booths.map(b => {
    const best = b.jobs.reduce((max, job) => {
      const { pct } = computeMatch(studentSkills, job);
      return pct > max ? pct : max;
    }, 0);
    return { ...b, bestMatch: best };
  });

  const sorted = [...boothsWithMatch].sort((a, b) => {
    if (sortBy === 'match') return b.bestMatch - a.bestMatch;
    if (sortBy === 'jobs') return b.jobs.length - a.jobs.length;
    return 0;
  });

  const topMatches = sorted.filter(b => b.bestMatch >= 70);
  const checklistItems = [
    { done: studentSkills.length > 0, label: 'Skills profile updated', sub: studentSkills.length > 0 ? `${studentSkills.length} skills added` : 'Add skills to your profile' },
    { done: hasResume, label: 'Resume uploaded', sub: hasResume ? 'Resume ready' : 'Upload your latest resume' },
    { done: !!cgpa && cgpa > 0, label: 'CGPA recorded', sub: cgpa > 0 ? `CGPA: ${cgpa}` : 'Update your academic details' },
    { done: !!degree, label: 'Degree information complete', sub: degree || 'Add your degree info' },
  ];
  const readinessScore = Math.round((checklistItems.filter(c => c.done).length / checklistItems.length) * 100);

  return (
    <div className="space-y-6">
      {/* Readiness banner */}
      <div className="relative overflow-hidden rounded-2xl p-5"
        style={{ background: 'linear-gradient(135deg, #0d1f4e 0%, #1a3884 60%, #2a52b0 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 10% 20%, white 1px, transparent 1px), radial-gradient(circle at 90% 80%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={15} className="text-blue-300" />
              <span className="text-[11px] font-black uppercase tracking-widest text-blue-300">Your Prep Kit</span>
            </div>
            <h3 className="text-xl font-black text-white leading-tight mb-1">
              Fair Readiness: {readinessScore}%
            </h3>
            <p className="text-sm text-blue-200 font-medium">
              {topMatches.length > 0
                ? `You're a strong match for ${topMatches.length} compan${topMatches.length === 1 ? 'y' : 'ies'} at this fair.`
                : 'Complete your profile to improve your match score.'}
            </p>
          </div>
          {/* Readiness circle */}
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
              <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
              <circle cx="28" cy="28" r="22" fill="none" stroke="white" strokeWidth="5"
                strokeDasharray={`${(readinessScore / 100) * 138.23} 138.23`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-base font-black text-white">
              {readinessScore}
            </span>
          </div>
        </div>

        {/* Readiness bar */}
        <div className="relative mt-4">
          <div className="h-2 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full rounded-full bg-white transition-all duration-700" style={{ width: `${readinessScore}%` }} />
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="rounded-2xl border p-5" style={{ borderColor: isDark ? 'rgba(26,56,132,0.3)' : '#e2eaff', background: isDark ? '#002147' : '#fff' }}>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 size={15} className="text-[#1a3884] dark:text-[#4f7dff]" />
          <h4 className="text-sm font-black text-gray-900 dark:text-white">Fair Day Checklist</h4>
        </div>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mb-3">Complete these before the fair day</p>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {checklistItems.map((item, i) => (
            <PrepCheckItem key={i} {...item} />
          ))}
        </div>
      </div>

      {/* Company recommendations */}
      {booths.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target size={15} className="text-[#1a3884] dark:text-[#4f7dff]" />
              <h4 className="text-sm font-black text-gray-900 dark:text-white">Companies at This Fair</h4>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-[#1a3884]/8 dark:bg-[#1a3884]/25 text-[#1a3884] dark:text-[#4f7dff]">
                {booths.length}
              </span>
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-[11px] font-bold border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-white dark:bg-[#001430] text-slate-700 dark:text-slate-200 focus:outline-none"
            >
              <option value="match">Best Match First</option>
              <option value="jobs">Most Jobs First</option>
            </select>
          </div>
          <div className="space-y-3">
            {sorted.map((booth, i) => (
              <CompanyMatchCard
                key={booth.entityId || i}
                booth={booth}
                studentSkills={studentSkills}
                cgpa={cgpa}
                isDark={isDark}
              />
            ))}
          </div>
        </div>
      )}

      {booths.length === 0 && (
        <div className="text-center py-10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
          <Briefcase size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
          <p className="text-sm font-semibold text-slate-400">No jobs posted yet — check back closer to the fair date.</p>
        </div>
      )}

      {/* Skill tip */}
      {studentSkills.length < 5 && (
        <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: isDark ? 'rgba(245,158,11,0.08)' : '#fffbeb', border: '1px solid rgba(245,158,11,0.25)' }}>
          <Zap size={16} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Boost your match score</p>
            <p className="text-[12px] text-amber-600 dark:text-amber-500 mt-0.5">
              You have {studentSkills.length} skill{studentSkills.length !== 1 ? 's' : ''} on your profile. Adding more skills significantly improves your match with recruiters.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPrepKit;
